import { useEffect, useRef, useState } from 'react';

export default function RootCauseSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="root-cause"
      className="py-24 px-4 md:px-10"
      style={{ backgroundColor: '#111111' }}
    >
      <div className="max-w-4xl mx-auto text-center">
        <div
          className="transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <span
            className="inline-block px-3 py-1 text-xs font-bold tracking-widest uppercase text-[#C0392B] border border-[#C0392B]/30 rounded-full mb-8"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Root Cause
          </span>

          <h2
            className="text-3xl md:text-5xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            原因は、<span className="text-[#C0392B]">「人」ではない。</span>
          </h2>
          <h2
            className="text-3xl md:text-5xl font-bold text-white leading-tight mb-10"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            「会議の<span className="text-[#C0392B]">設計</span>」である。
          </h2>

          <p
            className="text-base md:text-lg text-white/60 leading-loose max-w-2xl mx-auto mb-14"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            「参加者の意識が低い」「リーダーシップが足りない」は、表面的な診断です。<br />
            会議が機能しない本当の理由は、<strong className="text-white">意思決定のプロセスが設計されていないこと</strong>。<br />
            構造を変えれば、同じメンバーでも会議は変わります。
          </p>
        </div>

        {/* 3 Structural Problems */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 rounded-lg overflow-hidden transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transitionDelay: '0.3s' }}
        >
          {[
            {
              label: '目的設計なき会議',
              desc: '何を決めるための会議か、参加者全員が明確に認識していない',
              icon: 'ri-focus-3-line',
            },
            {
              label: '権限なき集まり',
              desc: '誰が最終決断を下すのか、会議のルールが定義されていない',
              icon: 'ri-key-line',
            },
            {
              label: '実行の断絶',
              desc: '決定事項が実行タスクに変換されず、会議の外で消えていく',
              icon: 'ri-link-unlink',
            },
          ].map((item, i) => (
            <div key={i} className="bg-white/5 p-8 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full border border-[#C0392B]/30">
                <i className={`${item.icon} text-2xl text-[#C0392B]`} />
              </div>
              <h3
                className="text-base font-bold text-white mb-3"
                style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
              >
                {item.label}
              </h3>
              <p className="text-sm text-white/50 leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                {item.desc}
              </p>
            </div>
          ))}
        </div>

        <div
          className="mt-12 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transitionDelay: '0.5s' }}
        >
          <p
            className="text-lg md:text-2xl font-bold text-white/80"
            style={{ fontFamily: 'Noto Sans JP, sans-serif' }}
          >
            会議は始まる前に、<span className="text-[#C0392B]">8割が決まっている。</span>
          </p>
        </div>
      </div>
    </section>
  );
}
